import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import Logger from './Logger';
import { Button, Modal, Typography, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';
import { usePlan } from '../context/PlanContext';
import PricingPlanCard from './PricingPlanCard';
import '../Css/PlanSelectModal.css';

const LoginPage = () => {
    const [activeTab, setActiveTab] = useState("login"); // 'login', 'signup', 'forgot'
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);
    const logger = React.useRef(new Logger(getLoggerBaseURL())).current;
    const { refreshBilling } = usePlan();
    const [isFormCompleted, setIsFormCompleted] = useState(false);
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [otp, setOtp] = useState('');
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [showPlanDisclaimer, setShowPlanDisclaimer] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlanOption, setselectedPlanOption] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        organization: '',
        phone: '',
        email: '',
        gst: '',
        upi: '',
        address: '',
        password: '',
        confirmPassword: '',
        referralCode: ''
    });
    /** Separate from signup so login credentials never fill Admin Registration. */
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [forgotEmail, setForgotEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [otpForgot, setOtpForgot] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const planModalBodyRef = useRef(null);

    const scrollPlanModalToTop = useCallback(() => {
        if (planModalBodyRef.current) {
            planModalBodyRef.current.scrollTop = 0;
        }
    }, []);

    const switchTab = (tab) => {
        if (tab === "signup") {
            setFormData((prev) => ({
                ...prev,
                email: "",
                password: "",
                confirmPassword: "",
            }));
            setIsOtpSent(false);
            setIsEmailVerified(false);
            setOtp("");
            setShowPlanDisclaimer(false);
            setselectedPlanOption(null);
        }
        setActiveTab(tab);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get("tab");
        if (tab === "signup") {
            switchTab("signup");
        }
    }, []);

    const fetchPlans = useCallback(async () => {
        try {
            const response = await fetch(`/api/plan/fetch`);
            setIsLoading(true);
            if (!response.ok) {
                throw new Error("Unable to fetch plans");
            }
            const data = await response.json();
            if (data === null || data.length === 0) {
                setPlans([]);
            } else {
                setPlans(data);
            }
        }
        catch (error) {
            logger.log(`Error fetching plans: ${error.message}`, 'ERROR');
            setPlans([]);
        } finally {
            setIsLoading(false);
        }
    }, [logger]);

    useEffect(() => {
        fetchPlans()
    }, [fetchPlans]);

    useEffect(() => {
        if (openConfirmationModal) {
            requestAnimationFrame(() => {
                scrollPlanModalToTop();
            });
        }
    }, [openConfirmationModal, showPlanDisclaimer, scrollPlanModalToTop]);

    useEffect(() => {
        const { name, organization, phone, email, password, confirmPassword, gst, address } = formData;
        setIsFormCompleted(Boolean(name && phone && email && password && confirmPassword && gst && address && organization));
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleLoginChange = (e) => {
        const { name, value } = e.target;
        setLoginData((prev) => ({ ...prev, [name]: value }));
    };

    const persistLoginSession = async (data) => {
        sessionStorage.setItem('userEmail', data?.user?.email ?? '');
        sessionStorage.setItem('adminEmail', data?.user?.email ?? '');

        if (data?.user?.pickup?.[0]) {
            sessionStorage.setItem('companyName', data.user.pickup[0].comName);
            sessionStorage.setItem('phoneNo', data.user.pickup[0].comPhone);
            sessionStorage.setItem('address', data.user.pickup[0].comAddress);
        }
        sessionStorage.setItem('role', data.role);
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('permissions', JSON.stringify(data.permissions) || JSON.stringify(data.user.permissions));
        await refreshBilling();
    };

    const navigateAfterLogin = (data) => {
        if (data.role === "packer") {
            navigate('/packerLanding');
        } else {
            navigate('/dashboard');
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (loginData.email === "hello@bntechnology.net" && loginData.password === "123456") {
            logger.log(`User email ${loginData.email} signed in successfully with marketing credentials`, 'INFO');
            navigate("/Marketing"); //Redirect to the marketing page
            return;
        }

        if (loginData.email === 'diyaag006@gmail.com' && loginData.password === 'deploy') {
            logger.log(`User email ${loginData.email} signed in successfully with special credentials`, 'INFO');
            navigate('/deploy'); // Redirect to the DeployComponent
            return;
        }

        try {
            const response = await fetch(`/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: loginData.email,
                    password: loginData.password,
                }),
            });
            setIsLoading(true);

            if (response.ok) {
                const data = await response.json();
                logger.log(`User email ${loginData.email} signed in successfully`, 'INFO');
                await persistLoginSession(data);
                navigateAfterLogin(data);
            } else {
                const errorData = await response.json();

                if (typeof errorData.error === 'string') {
                    setError(errorData.error);
                    toast.error(errorData?.error);
                    logger.log(`Failed login attempt for email: ${loginData.email} - Error: ${errorData.error}`, 'WARN');
                } else {
                    setError('Invalid Login Id Or Password.');
                    toast.error('Invalid Login Id Or Password.');
                    logger.log(`Failed login attempt for email: ${loginData.email} - Error: Invalid Login Id Or Password.`, 'WARN');
                }
            }
        } catch (err) {
            toast.error('Invalid Login Id Or Password.' || err.message);
            logger.log(`Unexpected error during login attempt for email: ${loginData.email} - Error: ${err.message}`, 'ERROR');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError("");

        if (!forgotEmail) {
            toast.error("Please enter your email");
            return;
        }

        try {
            logger.log(`Forgot password request for: ${forgotEmail}`, "INFO");

            const response = await fetch("/api/forgotPassword/forgotPasswords", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: forgotEmail })
            });
            setIsLoading(true);

            const data = await response.json();

            if (!response.ok) {
                toast.error(data.message || "Error sending OTP");
                logger.log(`Forgot Password failed: ${data.message}`, "WARN");
                return;
            }

            toast.success("OTP sent to your email");
            logger.log("Forgot Password OTP sent successfully", "INFO");

            setActiveTab("resetPassword");
        } catch (err) {
            toast.error(`Unexpected error : ${err.message}`);
            logger.log(`Forgot password error: ${err.message}`, "ERROR");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch("/api/forgotPassword/resetPassword", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: forgotEmail,
                    otp: otpForgot,
                    newPassword
                })
            });
            setIsLoading(true);

            const data = await response.json();

            if (!response.ok) {
                toast.error(data.message || "Invalid OTP");
                return;
            }

            toast.success("Password updated successfully");

            setOtpForgot("");
            setNewPassword("");
            setActiveTab("login");
        } catch (err) {
            toast.error("Error resetting password");
        } finally {
            setIsLoading(false);
        }
    };

    const validateGST = (gst) => {
        return /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}$/.test(gst);
    };

    const validateUPI = (upi) => {
        const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{3,64}$/;
        return upiRegex.test(upi);
    };

    const validateForm = () => {
        const { name, organization, phone, email, password, confirmPassword, gst, address, upi } = formData;

        if (!name) {
            toast.error('Name is required');
            return false;
        }

        if (!organization) {
            toast.error('Organization Name is required');
            return false;
        }

        if (!phone.match(/^\d{10}$/)) {
            toast.error('Phone number must be 10 digits');
            return false;
        }

        if (!email.includes('@')) {
            toast.error('Invalid email address');
            return false;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return false;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return false;
        }

        if (!validateGST(gst)) {
            toast.error('Invalid GST number. GST number should be 15 alphanumeric characters.');
            return false;
        }

        if (!validateUPI(upi)) {
            toast.error('Invalid UPI ID. Please enter a valid UPI ID.');
            return false;
        }

        if (!address) {
            toast.error('Address is required');
            return false;
        }

        return true;
    };

    const handleSendAndStoreOTP = async (event) => {
        event.preventDefault();

        if (!isFormCompleted) {
            toast.error('Please fill all the fields');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setError('');
        setIsSendingOtp(true);

        try {
            const response = await fetch(`/api/signup/send-store-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: String(formData.email || '').trim().toLowerCase(),
                    phone: formData.phone,
                    gst: formData.gst,
                    organization: formData.organization,
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error sending OTP. Please try again.');
            }

            const data = await response.json();
            logger.log(`OTP sent for registration! ${data}`, 'INFO');
            toast.success('OTP sent to your email. Please check your inbox.');

            if (response.status === 200) {
                setOtp('');
                setIsOtpSent(true);
            }
        } catch (error) {
            logger.log(`Error sending OTP: ${error.message}`, 'ERROR');
            toast.error(error.message || 'Error sending OTP. Please try again.');
        } finally {
            logger.log('Finished sending OTP', 'INFO');
            setIsSendingOtp(false);
        }
    };

    const handleVerifyOTP = async () => {
        setError('');

        const trimmedOtp = String(otp || '').trim();
        if (!trimmedOtp) {
            toast.error('Please enter the OTP sent to your email');
            return;
        }

        try {
            const response = await fetch(`/api/signup/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: String(formData.email || '').trim().toLowerCase(),
                    otp: trimmedOtp
                })
            });
            setIsLoading(true);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Invalid OTP. Please try again.');
            }

            const data = await response.json();
            logger.log(`OTP verified for registration ${data}`, 'INFO');

            if (response.status === 200) {
                toast.success('Email verified successfully.');
                setIsEmailVerified(true);
                setShowPlanDisclaimer(false);
                setOpenConfirmationModal(true);
            }
        } catch (error) {
            logger.log(`Error verifying OTP: ${error.message}`, 'ERROR');
            setError(error.message || 'Invalid OTP. Please try again.');
            toast.error(error.message || 'Invalid OTP. Please try again.');
        } finally {
            logger.log('Finished verifying OTP', 'INFO');
            setIsLoading(false);
        }
    };

    const closePlanModal = () => {
        setOpenConfirmationModal(false);
        setShowPlanDisclaimer(false);
    };

    const handleChoosePlan = (plan) => {
        setselectedPlanOption(plan);
        setShowPlanDisclaimer(true);
    };

    const handleBackToPlans = () => {
        setShowPlanDisclaimer(false);
    };

    const handleOptionSelect = async (plan) => {
        setselectedPlanOption(plan);
        setIsLoading(true);
        try {
            const payload = { ...formData, plan };
            const response = await fetch(`/api/signup/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                logger.log(`Registration failed: ${errorData.message || response.status}`, 'ERROR');
                toast.error(errorData.message || 'Registration failed. Please try again.');
                return;
            }

            logger.log(`Registration successful for ${formData.email}`, 'INFO');
            toast.success('Registration successful!');

            const email = String(formData.email || '').trim().toLowerCase();
            const loginResponse = await fetch(`/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    password: formData.password,
                }),
            });

            if (!loginResponse.ok) {
                toast.error('Account created. Please log in.');
                setOpenConfirmationModal(false);
                setShowPlanDisclaimer(false);
                setActiveTab('login');
                setLoginData({ email, password: '' });
                setOtp('');
                setIsOtpSent(false);
                setIsEmailVerified(false);
                return;
            }

            const session = await loginResponse.json();
            logger.log(`User email ${email} signed in after registration`, 'INFO');
            await persistLoginSession(session);
            setOpenConfirmationModal(false);
            setShowPlanDisclaimer(false);
            navigateAfterLogin(session);
        } catch (error) {
            logger.log(`Error during registration: ${error.message}`, 'ERROR');
            toast.error("Some error occured", error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <div className="min-h-screen flex flex-col md:flex-row bg-gray-100">
                {/* Left Side - Conditional Form Section */}
                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    pauseOnHover
                    toastClassName={(context) => {
                        const type = context?.type || "default";
                        return `${context?.defaultClassName || ""} custom-toast custom-toast-${type}`.trim();
                    }}
                    bodyClassName="custom-toast-body"
                    closeButton
                />
                <div className="w-full md:w-3/5 overflow-y-auto flex items-center justify-center p-5">
                    <div className="w-4/5  bg-white rounded-lg shadow-md p-5 px-8">
                        {activeTab === 'login' && (
                            <>
                                <h2 className="text-2xl font-bold text-gray-800 mb-6">Login to Your Account</h2>
                                <form onSubmit={handleLogin} className="space-y-5" autoComplete="on">

                                    {/* Email */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-600 mb-1 block">Email</label>
                                        <input
                                            name="email"
                                            type="email"
                                            placeholder="Enter your email"
                                            value={loginData.email}
                                            onChange={handleLoginChange}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            autoComplete="username"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-gray-600 mb-1 block">Password</label>
                                        <div className="relative">
                                            <input
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Enter your password"
                                                value={loginData.password}
                                                onChange={handleLoginChange}
                                                className="w-full px-4 py-2 border rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                autoComplete="current-password"
                                                required
                                            />
                                            <FontAwesomeIcon
                                                icon={showPassword ? faEyeSlash : faEye}
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute top-2.5 right-3 text-gray-500 cursor-pointer"
                                            />
                                        </div>
                                    </div>

                                    <button type="submit" style={{ backgroundColor: "#07a6ef" }} className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition" disabled={isLoading} >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : "Login"}
                                    </button>

                                    <div className="text-sm text-center text-gray-500">
                                        Don’t have an account?{' '}
                                        <span className="text-blue-600 hover:underline cursor-pointer" onClick={() => switchTab('signup')} >
                                            Sign up
                                        </span>
                                    </div>
                                    <div className="text-sm text-center text-gray-500 mt-1">
                                        Forgot your password?{' '}
                                        <span className="text-blue-600 hover:underline cursor-pointer" onClick={() => switchTab('forgot')}                                    >
                                            Reset here
                                        </span>
                                    </div>
                                </form>
                            </>
                        )}

                        {activeTab === 'signup' && (
                            <>
                                <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">Admin Registration</h2>
                                <form className="space-y-2" autoComplete="off">

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {/* Name */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="Enter your name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="name"
                                            />
                                        </div>

                                        {/* Organization */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Organization Name</label>
                                            <input
                                                type="text"
                                                name="organization"
                                                value={formData.organization}
                                                onChange={handleChange}
                                                placeholder="Enter organization name"
                                                className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="organization"
                                            />
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                placeholder="Enter your phone number"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="tel"
                                            />
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Enter your email"
                                                value={formData.email}
                                                disabled={isEmailVerified}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="off"
                                                data-lpignore="true"
                                                data-1p-ignore="true"
                                            />
                                        </div>

                                        {/* GST Number */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">GST Number</label>
                                            <input
                                                type="text"
                                                name="gst"
                                                placeholder="Enter your GST number"
                                                value={formData.gst}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="off"
                                            />
                                        </div>

                                        {/* UPI ID */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">UPI ID</label>
                                            <input
                                                type="text"
                                                name="upi"
                                                placeholder="Enter your UPI ID"
                                                value={formData.upi}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="off"
                                            />
                                        </div>

                                        {/* Password */}
                                        <div className="relative">
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="Enter your password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="new-password"
                                                data-lpignore="true"
                                                data-1p-ignore="true"
                                            />
                                            <FontAwesomeIcon
                                                icon={showPassword ? faEye : faEyeSlash}
                                                onClick={togglePasswordVisibility}
                                                className="absolute right-3 top-9 text-gray-500 cursor-pointer"
                                            />
                                        </div>

                                        {/* Confirm Password */}
                                        <div className="relative">
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Confirm Password</label>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                placeholder="Confirm your password"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                                autoComplete="new-password"
                                                data-lpignore="true"
                                                data-1p-ignore="true"
                                            />
                                            <FontAwesomeIcon
                                                icon={showConfirmPassword ? faEye : faEyeSlash}
                                                onClick={toggleConfirmPasswordVisibility}
                                                className="absolute right-3 top-9 text-gray-500 cursor-pointer"
                                            />
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-1 block">Address</label>
                                        <input
                                            type="text"
                                            name="address"
                                            placeholder="Enter your address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            className="w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                            autoComplete="off"
                                        />
                                    </div>

                                    {
                                        isFormCompleted && isEmailVerified && isOtpSent && !openConfirmationModal && (
                                            <button
                                                type="button"
                                                style={{ backgroundColor: "#07a6ef" }}
                                                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                                                onClick={() => {
                                                    setShowPlanDisclaimer(false);
                                                    setOpenConfirmationModal(true);
                                                }}
                                                disabled={isLoading}
                                            >
                                                Select a Plan
                                            </button>
                                        )
                                    }

                                    {
                                        !isOtpSent && <button
                                            type="submit"
                                            style={{ backgroundColor: "#07a6ef" }}
                                            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-70 disabled:cursor-not-allowed"
                                            onClick={(e) => handleSendAndStoreOTP(e)}
                                            disabled={isSendingOtp}
                                        >
                                            {isSendingOtp ? (
                                                <span className="inline-flex items-center justify-center gap-2">
                                                    <CircularProgress size={18} color="inherit" />
                                                    Sending OTP...
                                                </span>
                                            ) : (
                                                "Send OTP"
                                            )}
                                        </button>
                                    }

                                    <p className="text-sm text-center text-gray-500 mt-2">
                                        Already have an account?{' '}
                                        <span
                                            className="text-blue-600 hover:underline cursor-pointer"
                                            onClick={() => switchTab('login')}
                                        >
                                            Login here
                                        </span>
                                    </p>
                                </form>
                            </>
                        )}

                        {isOtpSent && !isEmailVerified && (
                            <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
                                <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md relative">
                                    <h3 className="text-xl font-semibold mb-4 text-center text-gray-800">Enter OTP</h3>

                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="Enter OTP"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.trim())}
                                        className="w-full border px-4 py-2 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        autoComplete="off"
                                    />

                                    <button
                                        onClick={handleVerifyOTP}
                                        className="w-full bg-blue-400 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                                    >
                                        Verify OTP
                                    </button>

                                    <button
                                        onClick={() => setIsOtpSent(false)}
                                        className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-lg"
                                        aria-label="Close"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'forgot' && (
                            <>
                                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Forgot Password</h2>
                                <form onSubmit={handleForgotPassword} className="space-y-4" autoComplete="off">
                                    <div>
                                        <label className="text-sm font-medium text-gray-600 mb-1 block">Email</label>
                                        <input
                                            type="email"
                                            name="forgotEmail"
                                            placeholder="Enter your email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        style={{ backgroundColor: "#07a6ef" }}
                                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                                    >
                                        Send Reset Link
                                    </button>

                                    <p className="text-sm text-center text-gray-500 mt-2">
                                        Go back to{' '}
                                        <span
                                            className="text-blue-600 hover:underline cursor-pointer"
                                            onClick={() => setActiveTab('login')}
                                        >
                                            Login
                                        </span>
                                    </p>
                                </form>
                            </>
                        )}

                        {activeTab === "resetPassword" && (
                            <>
                                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
                                    Reset Password
                                </h2>

                                <form onSubmit={handleResetPassword} className="space-y-4">

                                    {/* OTP FIELD */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-600 mb-1 block">
                                            Enter OTP
                                        </label>
                                        <input
                                            type="text"
                                            value={otpForgot}
                                            onChange={(e) => setOtpForgot(e.target.value)}
                                            placeholder="Enter OTP you received"
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    {/* NEW PASSWORD FIELD */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-600 mb-1 block">
                                            New Password
                                        </label>
                                        <input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                                    >
                                        Reset Password
                                    </button>

                                    <p className="text-sm text-center text-gray-500 mt-2">
                                        Go back to{" "}
                                        <span
                                            className="text-blue-600 hover:underline cursor-pointer"
                                            onClick={() => setActiveTab("login")}
                                        >
                                            Login
                                        </span>
                                    </p>
                                </form>
                            </>
                        )}
                    </div>
                </div>

                {/* Right Side - Shared Section */}
                <div className="w-full md:w-2/5 bg-[#07a6ef] flex items-center justify-center p-10">
                    <div className="text-center">
                        <img
                            src="https://www.zordigo.com/assets/Zordigo%20Logo.jpeg"
                            alt="Zordigo Logo"
                            className="w-80 h-auto rounded-lg shadow-lg mx-auto mb-6"
                        />
                        <h3 className="text-2xl font-bold text-white mb-2">Welcome to Zordigo</h3>
                        <p className="text-white">
                            Manage your orders, drivers, and deliveries efficiently using our powerful admin panel.
                        </p>
                    </div>
                </div>

                <Modal
                    open={openConfirmationModal}
                    onClose={closePlanModal}
                    className="plan-select-overlay"
                >
                    <div
                        className={`plan-select-modal${showPlanDisclaimer ? " plan-select-modal--disclaimer" : ""}`}
                        role="dialog"
                        aria-labelledby="plan-select-title"
                    >
                        <div className="plan-select-header">
                            <IconButton
                                aria-label="Close"
                                onClick={closePlanModal}
                                size="small"
                                className="plan-select-close"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <h2 id="plan-select-title" className="plan-select-heading">
                                {showPlanDisclaimer ? "Plan Disclaimer" : "Select a Plan"}
                            </h2>
                            <p className="plan-select-sub">
                                {showPlanDisclaimer
                                    ? "Review the features you are opting for with this plan before you continue."
                                    : "Choose a monthly plan that fits your delivery operations. Advance Plan includes your first full month free."}
                            </p>
                        </div>
                        <div className="plan-select-body" ref={planModalBodyRef}>
                            {showPlanDisclaimer && selectedPlanOption ? (
                                <div className="plan-disclaimer">
                                    <p className="plan-disclaimer-intro">
                                        You are about to continue with the following plan. Please confirm you understand
                                        the included features and terms.
                                    </p>
                                    <PricingPlanCard plan={selectedPlanOption} />
                                    <div className="plan-disclaimer-actions">
                                        <button
                                            type="button"
                                            className="plan-disclaimer-back"
                                            onClick={handleBackToPlans}
                                            disabled={isLoading}
                                        >
                                            Go Back
                                        </button>
                                        <button
                                            type="button"
                                            className="plan-disclaimer-proceed"
                                            onClick={() => handleOptionSelect(selectedPlanOption)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? "Please wait…" : "Proceed"}
                                        </button>
                                    </div>
                                </div>
                            ) : plans.length > 0 ? (
                                <div className="plan-select-grid">
                                    {[...plans]
                                        .sort((a, b) => {
                                            const rank = (p) => {
                                                const n = String(p.planName || "").toLowerCase();
                                                if (n === "starter") return 1;
                                                if (n === "advance") return 2;
                                                if (n === "custom" || p.isCustom) return 3;
                                                return 4;
                                            };
                                            return rank(a) - rank(b);
                                        })
                                        .filter((p) => {
                                            const n = String(p.planName || "").toLowerCase();
                                            return (
                                                n === "starter" ||
                                                n === "advance" ||
                                                n === "custom" ||
                                                p.isCustom === true
                                            );
                                        })
                                        .map((plan) => (
                                            <PricingPlanCard
                                                key={plan._id || plan.planNumber}
                                                plan={plan}
                                                action={
                                                    <Button
                                                        className="plan-select-btn"
                                                        fullWidth
                                                        disableElevation
                                                        onClick={() => handleChoosePlan(plan)}
                                                    >
                                                        Select {plan.planName}
                                                    </Button>
                                                }
                                            />
                                        ))}
                                </div>
                            ) : (
                                <p className="plan-select-empty">No plans available</p>
                            )}
                        </div>
                    </div>
                </Modal>
            </div>
            {error && (
                <Typography color="error" style={{ textAlign: 'center', marginTop: '20px' }}>
                    {error}
                </Typography>
            )}
        </>
    );
};

export default LoginPage;